/**
 * Nigerian phone number normalisation.
 *
 * The same number reaches us in at least three shapes — 08031234567,
 * +2348031234567, 2348031234567 — and users are inconsistent about spaces,
 * dashes and brackets. Every one of those must resolve to a single stored
 * form, because `User.phone` is unique: without this, the same person can
 * register twice in two formats and the one-account-per-number rule (decision
 * D1) silently does nothing.
 *
 * Canonical form is the local one: 11 digits beginning 0, e.g. 08031234567.
 * It is what people recognise, what they will read back over the phone, and
 * what the SMS provider accepts.
 */

/** Nigerian mobile network prefixes, without the leading zero. */
const MOBILE_PREFIX = /^[789][01]\d$/;

export type NormalisedPhone = string;

/**
 * Returns the canonical local form, or null when the input cannot be a
 * Nigerian mobile number. Callers must treat null as a validation failure
 * rather than storing the raw input.
 */
export function normalisePhone(input: string | null | undefined): NormalisedPhone | null {
  if (!input) return null;

  // Strip everything that is not a digit or a leading plus.
  const cleaned = input.trim().replace(/[\s()\-.]/g, "");
  if (!/^\+?\d+$/.test(cleaned)) return null;

  let digits = cleaned.startsWith("+") ? cleaned.slice(1) : cleaned;

  // 234XXXXXXXXXX -> 0XXXXXXXXXX
  if (digits.startsWith("234")) {
    digits = `0${digits.slice(3)}`;
  }

  // Bare 10-digit national number, missing its trunk zero: 8031234567
  if (digits.length === 10 && !digits.startsWith("0")) {
    digits = `0${digits}`;
  }

  if (digits.length !== 11 || !digits.startsWith("0")) return null;
  if (!MOBILE_PREFIX.test(digits.slice(1, 4))) return null;

  return digits;
}

/** True when two inputs refer to the same number, whatever shape they arrived in. */
export function isSamePhone(a: string | null | undefined, b: string | null | undefined) {
  const left = normalisePhone(a);
  const right = normalisePhone(b);
  return left !== null && left === right;
}

/** International form for providers that require it: +2348031234567. */
export function toInternational(input: string | null | undefined): string | null {
  const local = normalisePhone(input);
  return local ? `+234${local.slice(1)}` : null;
}

/** Masks all but the last four digits, for lists and logs: 0803****567. */
export function maskPhone(input: string | null | undefined): string {
  const local = normalisePhone(input);
  if (!local) return "";
  return `${local.slice(0, 4)}****${local.slice(-3)}`;
}
